import { useState, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface ComboboxOption {
  id: string; // stable key + selection value
  label: string; // visible text (also what cmdk filters on)
  sublabel?: string; // muted suffix (e.g. brand)
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string; // selected option id ("" = none)
  onSelect: (id: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  dense?: boolean;
  invalid?: boolean;
  // Amber "required but empty" border (e.g. ColorSelect isMissing). `invalid` (red) wins over it.
  warning?: boolean;
  emptyText?: string;
  // Optional custom trigger display; defaults to the selected option's label or the placeholder.
  triggerLabel?: ReactNode;
  // Forwarded to the trigger button so a <label htmlFor> can target it.
  id?: string;
  // ── Async / server-driven search (e.g. CustomerSelect) ──
  // When `onSearchChange` is given the search box becomes controlled; pair with `shouldFilter={false}`
  // so cmdk shows the server results verbatim instead of re-filtering them client-side.
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  // cmdk built-in fuzzy filtering. true (default) for static option lists; false for server results.
  shouldFilter?: boolean;
  // Show a loading row in the list while results are being fetched (async mode).
  loadingList?: boolean;
  loadingText?: string;
  // Notified when the popover opens/closes (e.g. to clear the async query on close).
  onOpenChange?: (open: boolean) => void;
}

// Themed searchable select: Radix Popover (escapes overflow/clip ancestors + keyboard/focus
// handling) + cmdk (fuzzy filter). Replaces the hand-rolled portal combobox in ProductSelect.
// Brand tokens only. Trigger width drives the panel width via --radix-popover-trigger-width.
export function Combobox({
  options,
  value,
  onSelect,
  placeholder,
  searchPlaceholder,
  isLoading = false,
  disabled = false,
  dense = false,
  invalid = false,
  warning = false,
  emptyText = "—",
  triggerLabel,
  id,
  searchValue,
  onSearchChange,
  shouldFilter = true,
  loadingList = false,
  loadingText = "...",
  onOpenChange,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  const display = triggerLabel ?? selected?.label ?? "";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled || isLoading}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-control border bg-white text-left outline-none transition-colors",
            "focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
            dense ? "px-2 py-1 text-[12px]" : "px-3.5 py-2.5 text-[14px]",
            // invalid (red) wins over warning (amber); both fall through to the neutral default.
            invalid
              ? "border-red focus-visible:border-red focus-visible:ring-red-border"
              : warning
                ? "border-warning-border hover:border-warning-border focus-visible:border-green focus-visible:ring-green"
                : "border-grey-200 hover:border-grey-300 focus-visible:border-green focus-visible:ring-green",
          )}
        >
          <span className={cn("truncate", display ? "text-grey-800" : "text-grey-400")}>
            {isLoading ? "..." : display || placeholder}
          </span>
          <ChevronsUpDown size={dense ? 13 : 15} strokeWidth={1.8} className="shrink-0 text-grey-400" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[1000] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-card border border-grey-200 bg-white shadow-float",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          )}
        >
          <Command className="w-full" shouldFilter={shouldFilter}>
            <div className="border-b border-grey-100 px-2 py-2">
              <Command.Input
                autoFocus
                placeholder={searchPlaceholder}
                {...(onSearchChange
                  ? { value: searchValue ?? "", onValueChange: onSearchChange }
                  : {})}
                className="w-full rounded-control border border-grey-200 px-3 py-1.5 text-[13px] text-grey-800 outline-none placeholder:text-grey-400 focus-visible:border-green focus-visible:ring-1 focus-visible:ring-green"
              />
            </div>
            <Command.List className="max-h-48 overflow-y-auto p-1">
              {loadingList && options.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-grey-400">{loadingText}</div>
              ) : (
                <Command.Empty className="px-4 py-3 text-[13px] text-grey-400">
                  {emptyText}
                </Command.Empty>
              )}
              {options.map((opt) => (
                <Command.Item
                  key={opt.id}
                  // cmdk filters on this string; include id so same-label options stay unique.
                  value={`${opt.label} ${opt.sublabel ?? ""} ${opt.id}`}
                  onSelect={() => {
                    onSelect(opt.id);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer select-none items-center justify-between gap-2 rounded-[6px] px-3 py-2 text-[13px] text-grey-800 outline-none data-[selected=true]:bg-green-bg"
                >
                  <span className="truncate">
                    {opt.label}
                    {opt.sublabel && (
                      <span className="ml-1.5 text-[11px] text-grey-400">{opt.sublabel}</span>
                    )}
                  </span>
                  {opt.id === value && (
                    <Check size={14} strokeWidth={2} className="shrink-0 text-green" />
                  )}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
